// ===========================================
// COLLAPSIBLE GUIDE LAYOUT - MUI VERSION
// Modern two-column layout with smooth animations
// ===========================================

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Typography,
  Fade,
  alpha,
} from '@mui/material';
import {
  Lightbulb as LightbulbIcon,
  ChevronRight as ChevronRightIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { COLORS } from '../../theme/constants';

const ANIMATION_DURATION = 350;

const CollapsibleGuideLayout = ({
  leftContent,
  rightContent,
  guideHeading = 'Setup Guidelines',
  defaultGuideWidth = 380,
  minGuideWidth = 280,
  maxGuideWidth = 500,
  defaultVisible = true,
  contentHeight = '100%',
}) => {
  const isAutoHeight = contentHeight === 'auto';
  const [isGuideVisible, setIsGuideVisible] = useState(defaultVisible);
  const [guideWidth, setGuideWidth] = useState(defaultGuideWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showContent, setShowContent] = useState(defaultVisible);

  const toggleGuideVisibility = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    
    if (isGuideVisible) {
      setShowContent(false);
      setTimeout(() => {
        setIsGuideVisible(false);
        setIsAnimating(false);
      }, ANIMATION_DURATION / 2);
    } else {
      setIsGuideVisible(true);
      setTimeout(() => {
        setShowContent(true);
        setIsAnimating(false);
      }, ANIMATION_DURATION / 2);
    }
  };

  const startResizing = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resizeGuide = useCallback(
    (e) => {
      if (isResizing) {
        const newGuideWidth = window.innerWidth - e.clientX - 64;
        const limitedGuideWidth = Math.min(
          Math.max(newGuideWidth, minGuideWidth),
          maxGuideWidth
        );
        setGuideWidth(limitedGuideWidth);
      }
    },
    [isResizing, minGuideWidth, maxGuideWidth]
  );

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', resizeGuide);
      document.addEventListener('mouseup', stopResizing);
    }
    return () => {
      document.removeEventListener('mousemove', resizeGuide);
      document.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing, resizeGuide, stopResizing]);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        height: contentHeight,
        overflow: isAutoHeight ? 'visible' : 'hidden',
        position: 'relative',
      }}
    >
      {/* Left Content Panel - Scrollable */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: isAutoHeight ? 'auto' : '100%',
          overflow: isAutoHeight ? 'visible' : 'auto',
          transition: `all ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: COLORS.background.subtle,
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.border.main,
            borderRadius: 4,
            '&:hover': {
              backgroundColor: COLORS.text.disabled,
            },
          },
        }}
      >
        {leftContent}
      </Box>

      {/* Resize Handle */}
      <Box
        onMouseDown={startResizing}
        sx={{
          width: isGuideVisible ? 12 : 0,
          cursor: 'col-resize',
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: `all ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          opacity: isGuideVisible ? 1 : 0,
          '&:hover': {
            '& .resize-bar': {
              backgroundColor: COLORS.primary.main,
              height: 80,
            },
          },
        }}
      >
        <Box
          className="resize-bar"
          sx={{
            width: 4,
            height: 48,
            backgroundColor: COLORS.border.main,
            borderRadius: 2,
            transition: 'all 0.2s ease',
            opacity: isGuideVisible ? 1 : 0,
          }}
        />
      </Box>

      {/* Right Guide Panel Container */}
      <Box
        sx={{
          width: isGuideVisible ? guideWidth : 48,
          minWidth: isGuideVisible ? minGuideWidth : 48,
          maxWidth: isGuideVisible ? maxGuideWidth : 48,
          minHeight: 0,
          flexShrink: 0,
          height: isAutoHeight ? 'calc(100vh - 220px)' : '100%',
          alignSelf: isAutoHeight ? 'flex-start' : 'stretch',
          transition: isResizing ? 'none' : `all ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          borderLeft: `1px solid ${COLORS.border.light}`,
          background: isGuideVisible 
            ? 'linear-gradient(180deg, #F0F9FF 0%, #FAFCFF 100%)'
            : COLORS.background.subtle,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          top: isAutoHeight ? 0 : 'auto',
          boxShadow: isGuideVisible ? '-4px 0 24px rgba(0, 0, 0, 0.06)' : 'none',
        }}
      >
        {/* Expanded: flex column fills panel height (avoids absolute + Fade height gaps) */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Fade in={showContent && isGuideVisible} timeout={ANIMATION_DURATION} unmountOnExit>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
            {/* Guide Header */}
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2.5,
                py: 2,
                borderBottom: `1px solid ${alpha(COLORS.primary.main, 0.15)}`,
                background: 'linear-gradient(135deg, #F0F9FF 0%, #EEF2FF 100%)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${COLORS.primary.main}, ${alpha(COLORS.primary.main, 0.7)})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${alpha(COLORS.primary.main, 0.3)}`,
                  }}
                >
                  <LightbulbIcon sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Typography
                  sx={{ fontWeight: 600, color: COLORS.text.primary, fontSize: '0.95rem' }}
                >
                  {guideHeading}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={toggleGuideVisibility}
                endIcon={<ChevronRightIcon />}
                disabled={isAnimating}
                sx={{
                  color: COLORS.text.secondary,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  px: 1.5,
                  borderRadius: 1.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(COLORS.primary.main, 0.1),
                    color: COLORS.primary.main,
                  },
                }}
              >
                Hide
              </Button>
            </Box>

            {/* Guide Content - Scrollable */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 2.5,
                py: 2,
                boxSizing: 'border-box',
                '&::-webkit-scrollbar': {
                  width: 6,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: alpha(COLORS.primary.main, 0.05),
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: alpha(COLORS.primary.main, 0.2),
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: alpha(COLORS.primary.main, 0.3),
                  },
                },
              }}
            >
              {rightContent}
            </Box>
          </Box>
        </Fade>
        </Box>

        {/* Collapsed State - show button */}
        <Fade in={!isGuideVisible} timeout={ANIMATION_DURATION}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              boxShadow: '2px 2px 10px 0px #00000014 inset',
            }}
          >
            <Button
              variant="text"
              size="small"
              startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
              onClick={toggleGuideVisibility}
              disabled={isAnimating}
              sx={{
                position: 'absolute',
                left: '50%',
                top: 56,
                transform: 'translateX(-50%) rotate(90deg)',
                transformOrigin: 'center center',
                whiteSpace: 'nowrap',
                minWidth: 'max-content',
                textTransform: 'none',
                color: COLORS.primary.main,
                px: 1,
                zIndex: 2,
                '&:hover': {
                  backgroundColor: alpha(COLORS.primary.main, 0.1),
                },
              }}
            >
              Show Guidelines
            </Button>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

CollapsibleGuideLayout.propTypes = {
  leftContent: PropTypes.node.isRequired,
  rightContent: PropTypes.node.isRequired,
  guideHeading: PropTypes.string,
  defaultGuideWidth: PropTypes.number,
  minGuideWidth: PropTypes.number,
  maxGuideWidth: PropTypes.number,
  defaultVisible: PropTypes.bool,
  contentHeight: PropTypes.string,
};

export default CollapsibleGuideLayout;
